import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgIf } from '@angular/common';
import { LazyLoadingVerifier } from './lazy-loading-verifier';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';

/**
 * Debug component to visualize lazy loading status
 * This component shows which modules have been loaded and when
 */
@Component({
  selector: 'unified-ui-lazy-loading-debug',
  standalone: true,
  imports: [NgIf, ButtonModule, CardModule, TableModule],
  template: `
    <div *ngIf="isVisible" class="lazy-loading-debug">
      <p-card header="Lazy Loading Status" styleClass="shadow-2 mb-4">
        <p-table [value]="loadedModules" styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr>
              <th>Module</th>
              <th>Loaded At</th>
              <th>Approximate Size</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-module>
            <tr>
              <td>{{ module.name }}</td>
              <td>{{ module.timestamp }}</td>
              <td>{{ module.approximateSize }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="footer">
            <tr>
              <td colspan="3">
                <strong>Total Modules Loaded:</strong> {{ loadedModules.length }}
              </td>
            </tr>
          </ng-template>
        </p-table>
        <div class="flex justify-content-end mt-3">
          <p-button icon="pi pi-times" label="Close" (onClick)="toggleVisibility()" styleClass="p-button-sm"></p-button>
        </div>
      </p-card>
    </div>
  `,
  styles: [`
    .lazy-loading-debug {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 1000;
      max-width: 500px;
      max-height: 400px;
      overflow: auto;
      background-color: white;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    }
  `]
})
export class LazyLoadingDebugComponent implements OnInit, OnDestroy {
  isVisible = false;
  loadedModules: Array<{ name: string; timestamp: string; approximateSize: string }> = [];
  private intervalId: any;

  ngOnInit(): void {
    // Add keyboard shortcut (Ctrl+Shift+L) to toggle visibility
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
    
    // Update the loaded modules list periodically
    this.updateLoadedModules();
    this.intervalId = setInterval(() => {
      this.updateLoadedModules();
    }, 2000);
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  toggleVisibility(): void {
    this.isVisible = !this.isVisible;
    if (this.isVisible) {
      this.updateLoadedModules();
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    // Ctrl+Shift+L to toggle the debug panel
    if (event.ctrlKey && event.shiftKey && event.key === 'L') {
      event.preventDefault();
      this.toggleVisibility();
    }
  }

  private updateLoadedModules(): void {
    const modules = LazyLoadingVerifier.getLoadedModules();
    this.loadedModules = Object.entries(modules).map(([name, details]) => ({
      name,
      timestamp: details.timestamp,
      approximateSize: details.approximateSize
    }));
  }
}
